import { displayDate } from "src/utils/dateHandler.util";
import { formatScreamingSnakeCase } from "src/utils/util";
import { CreditVoucher } from "../types/credit-voucher.entity";
import { Amount } from "src/components/Amount";
import { CreditVoucherViewButton } from "./credit-voucher-view-button";

type Props = {
  data: CreditVoucher[];
};

const statusClass = (status: CreditVoucher["status"]) =>
  status === "REDEEMED"
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-amber-100 text-amber-700 border-amber-200";

const typeClass = (type: CreditVoucher["type"]) => {
  switch (type) {
    case "PROMO":
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    case "TOP_UP":
      return "bg-sky-100 text-sky-700 border-sky-200";
    case "PRIVATE_MODE":
      return "bg-violet-100 text-violet-700 border-violet-200";
    case "MONTHLY_PREMIUM":
    case "YEARLY_PREMIUM":
    case "PREMIUM":
      return "bg-indigo-100 text-indigo-700 border-indigo-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const CreditVouchersTable: React.FC<Props> = ({ data }) => {
  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[980px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Voucher</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Type</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Amount</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Created</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Expiry</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-4 text-xl text-gray-500">
                No Credit Vouchers
              </td>
            </tr>
          )}

          {data.map((record) => (
            <tr key={record.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex flex-col items-center gap-1 leading-tight">
                  <span className="font-mono font-semibold text-[11px] sm:text-xs text-gray-800 break-all">{record.code || "--"}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">ID: {record.id}</span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className={`px-2 py-1 rounded-full border text-xs ${statusClass(record.status)}`}>
                  {formatScreamingSnakeCase(record.status)}
                </span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className={`px-2 py-1 rounded-full border text-xs ${typeClass(record.type)}`}>
                  {formatScreamingSnakeCase(record.type)}
                </span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <Amount amount={record.amount} currency={record.currency} />
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{displayDate(record.createdAt)}</span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{displayDate(record.expiryDate)}</span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <CreditVoucherViewButton creditVoucher={record} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CreditVouchersTable;
